import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Check } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { usePageHeaderContext } from "@/contexts/PageHeaderContext";
import { cn } from "@/lib/utils";

export function MobilePageNav() {
  const { title, tabs, currentTab, onTabChange } = usePageHeaderContext();
  const [open, setOpen] = useState(false);

  if (!title) return null;

  const hasTabs = tabs.length > 0;

  const handleTabSelect = (value: string) => {
    onTabChange?.(value);
    setOpen(false);
  };

  if (!hasTabs) {
    return (
      <span className="truncate text-sm font-medium text-foreground max-w-[200px]">
        {title}
      </span>
    );
  }

  const currentTabLabel = tabs.find((t) => t.value === currentTab)?.label;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={cn(
          "flex items-center gap-1 rounded-md px-2 py-1 -mx-2 -my-1",
          "text-sm font-medium text-foreground",
          "hover:bg-accent hover:text-accent-foreground",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          "transition-colors min-w-0",
        )}
      >
        <span className="truncate max-w-[140px]">{title}</span>
        {currentTabLabel && (
          <>
            <span className="text-muted-foreground shrink-0">/</span>
            <span className="text-muted-foreground truncate max-w-[80px]">
              {currentTabLabel}
            </span>
          </>
        )}
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0 ml-0.5" />
      </button>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent>
          <DrawerHeader className="pb-2">
            <DrawerTitle className="text-base">{title}</DrawerTitle>
          </DrawerHeader>
          <nav className="px-4 pb-6">
            <ul className="space-y-1">
              {tabs.map((tab) => {
                const className = cn(
                  "w-full flex items-center justify-between rounded-lg px-3 py-3",
                  "text-sm font-medium text-left",
                  "transition-colors",
                  tab.disabled
                    ? "text-muted-foreground cursor-not-allowed opacity-50"
                    : "hover:bg-accent hover:text-accent-foreground",
                  tab.value === currentTab &&
                    "bg-accent text-accent-foreground",
                );
                const content = (
                  <>
                    <span>{tab.label}</span>
                    {tab.value === currentTab && (
                      <Check className="h-4 w-4 shrink-0" />
                    )}
                  </>
                );

                return (
                  <li key={tab.value}>
                    {tab.href && !tab.disabled ? (
                      <Link
                        to={tab.href}
                        onClick={() => setOpen(false)}
                        className={className}
                      >
                        {content}
                      </Link>
                    ) : (
                      <button
                        onClick={() => handleTabSelect(tab.value)}
                        disabled={tab.disabled}
                        className={className}
                      >
                        {content}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
        </DrawerContent>
      </Drawer>
    </>
  );
}
